import React, { useCallback, useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';
import { SimulationNodeDatum } from 'd3';
import api from '../api/axiosConfig';
import { sanitizeTooltipHtml } from '../utils/sanitize';
import { useLanguage } from '../i18n/LanguageContext';
import './DiscoverGraph.css';

// The merged Discover + Visualization view: the selected note is the root
// node, its related notes branch off it, and clicking any node fetches and
// branches out *its* related notes — recursive exploration of the graph.

interface TagDetail {
  id: number;
  name: string;
  color: string;
  text_color: string;
}

interface EntryLike {
  id: number;
  content: string;
  content_preview?: string;
  tag_details: TagDetail[];
}

interface RelatedResponseItem extends EntryLike {
  score: number;
  shared_tags: TagDetail[];
}

interface GNode extends SimulationNodeDatum {
  id: number;
  label: string;
  content: string;
  color: string;
  textColor: string;
  tagDetails: TagDetail[];
  isRoot: boolean;
  expanded: boolean;
  loading: boolean;
}

interface GLink {
  source: number | GNode;
  target: number | GNode;
  sharedTags: TagDetail[];
  score: number;
  color: string;
}

interface DiscoverGraphProps {
  root: EntryLike;
}

const stripContent = (html: string): string =>
  html
    .replace(/\[\[image\]\]/g, '')
    .replace(/\[\[metadata\]\]:\s*/g, '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const makeNode = (entry: EntryLike, isRoot: boolean): GNode => {
  const text = stripContent(entry.content);
  const firstTag = entry.tag_details?.[0];
  return {
    id: entry.id,
    label: text.substring(0, 26) + (text.length > 26 ? '…' : ''),
    content: text,
    color: firstTag?.color || '#999',
    textColor: firstTag?.text_color || '#fff',
    tagDetails: entry.tag_details || [],
    isRoot,
    expanded: false,
    loading: false,
  };
};

const linkKey = (a: number, b: number) => (a < b ? `${a}-${b}` : `${b}-${a}`);

const DiscoverGraph: React.FC<DiscoverGraphProps> = ({ root }) => {
  const { t } = useLanguage();
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const simulationRef = useRef<d3.Simulation<GNode, undefined> | null>(null);
  const nodesRef = useRef<Map<number, GNode>>(new Map());
  const linksRef = useRef<Map<string, GLink>>(new Map());
  const zoomTransformRef = useRef<d3.ZoomTransform | null>(null);
  const [version, setVersion] = useState(0); // bump to re-render the graph
  const [emptyRoot, setEmptyRoot] = useState(false);
  const [tooltip, setTooltip] = useState<{
    visible: boolean; x: number; y: number; content: string;
  }>({ visible: false, x: 0, y: 0, content: '' });

  // Fetch a node's related entries and branch them off it.
  const expandNode = useCallback(async (entryId: number) => {
    const node = nodesRef.current.get(entryId);
    if (!node || node.expanded || node.loading) return;
    node.loading = true;
    setVersion(v => v + 1);
    try {
      const response = await api.get(`/entries/${entryId}/related/`);
      const related: RelatedResponseItem[] = response.data.related || [];
      related.forEach(item => {
        if (!nodesRef.current.has(item.id)) {
          const child = makeNode(item, false);
          // Spawn near the parent so the simulation unfolds outward
          child.x = (node.x ?? 0) + (Math.random() - 0.5) * 60;
          child.y = (node.y ?? 0) + (Math.random() - 0.5) * 60;
          nodesRef.current.set(item.id, child);
        }
        const key = linkKey(entryId, item.id);
        if (!linksRef.current.has(key)) {
          const rarest = item.shared_tags[0];
          linksRef.current.set(key, {
            source: entryId,
            target: item.id,
            sharedTags: item.shared_tags,
            score: item.score,
            color: rarest?.color || '#555',
          });
        }
      });
      node.expanded = true;
      if (node.isRoot && related.length === 0) setEmptyRoot(true);
    } catch (err) {
      console.error('Failed to expand node:', err);
    } finally {
      node.loading = false;
      setVersion(v => v + 1);
    }
  }, []);

  // Reset the graph whenever the root note changes.
  useEffect(() => {
    nodesRef.current = new Map();
    linksRef.current = new Map();
    zoomTransformRef.current = null;
    setEmptyRoot(false);
    const rootNode = makeNode(root, true);
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      rootNode.x = rect.width / 2;
      rootNode.y = rect.height / 2;
    }
    nodesRef.current.set(root.id, rootNode);
    setVersion(v => v + 1);
    expandNode(root.id);
  }, [root, expandNode]);

  // (Re)draw on every graph change. Node objects persist across renders,
  // so positions carry over and expansion feels continuous.
  useEffect(() => {
    if (!svgRef.current || !containerRef.current) return;
    if (simulationRef.current) simulationRef.current.stop();

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const rect = containerRef.current.getBoundingClientRect();
    const width = Math.max(rect.width, 400);
    const height = Math.max(rect.height, 400);
    svg.attr('viewBox', `0 0 ${width} ${height}`);

    const g = svg.append('g');
    const zoom = d3.zoom<SVGSVGElement, unknown>()
      .scaleExtent([0.15, 4])
      .on('zoom', (event) => {
        zoomTransformRef.current = event.transform;
        g.attr('transform', event.transform);
      });
    svg.call(zoom);
    if (zoomTransformRef.current) {
      svg.call(zoom.transform, zoomTransformRef.current);
    }

    const nodes = Array.from(nodesRef.current.values());
    const links = Array.from(linksRef.current.values());
    const maxScore = Math.max(...links.map(l => l.score), 0.001);

    const linkElements = g.append('g')
      .selectAll('line')
      .data(links)
      .enter()
      .append('line')
      .attr('stroke', d => d.color)
      .attr('stroke-width', d => 1 + (d.score / maxScore) * 3)
      .attr('stroke-opacity', 0.55)
      .attr('vector-effect', 'non-scaling-stroke');

    const nodeElements = g.append('g')
      .selectAll('g')
      .data(nodes, (d: any) => d.id)
      .enter()
      .append('g')
      .style('cursor', 'pointer')
      .call(d3.drag<SVGGElement, GNode>()
        .on('start', function (event, d) {
          if (!event.active) simulationRef.current?.alphaTarget(0.3).restart();
          d3.select(this).raise();
          d.fx = d.x;
          d.fy = d.y;
        })
        .on('drag', function (event, d) {
          d.fx = event.x;
          d.fy = event.y;
        })
        .on('end', function (event, d) {
          if (!event.active) simulationRef.current?.alphaTarget(0);
          d.fx = null;
          d.fy = null;
        }))
      .on('click', (event, d) => {
        event.stopPropagation();
        expandNode(d.id);
      })
      .on('mouseover', (event, d) => {
        const svgRect = svgRef.current!.getBoundingClientRect();
        // sanitizeTooltipHtml only lets style attributes through, so the
        // generated pieces are styled inline (same as the old panel).
        let tagsHTML = '';
        if (d.tagDetails.length > 0) {
          tagsHTML = '<div style="display:flex;flex-wrap:wrap;gap:4px;margin-top:8px;">';
          d.tagDetails.forEach(tag => {
            tagsHTML += `<span style="padding:2px 8px;border-radius:10px;font-size:0.7rem;font-weight:600;background-color:${tag.color || '#999'};color:${tag.text_color || '#fff'};">${tag.name}</span>`;
          });
          tagsHTML += '</div>';
        }
        const hint = d.expanded
          ? ''
          : `<div style="margin-top:8px;font-style:italic;font-size:0.72rem;opacity:0.75;">${t.discover.graphClickToBranch}</div>`;
        setTooltip({
          visible: true,
          x: event.clientX - svgRect.left + 12,
          y: event.clientY - svgRect.top - 10,
          content: sanitizeTooltipHtml(
            `<div>${d.content.substring(0, 400)}</div>${tagsHTML}${hint}`
          ),
        });
      })
      .on('mousemove', (event) => {
        const svgRect = svgRef.current!.getBoundingClientRect();
        setTooltip(t => t.visible
          ? { ...t, x: event.clientX - svgRect.left + 12, y: event.clientY - svgRect.top - 10 }
          : t);
      })
      .on('mouseout', () => setTooltip(t => ({ ...t, visible: false })));

    nodeElements.append('circle')
      .attr('r', d => d.isRoot ? 26 : 14)
      .attr('fill', d => d.color)
      .attr('stroke', d => d.isRoot ? 'var(--accent-primary, #5b94f1)' : 'rgba(255,255,255,0.35)')
      .attr('stroke-width', d => d.isRoot ? 3.5 : 1.5)
      .attr('opacity', d => d.loading ? 0.5 : 1);

    // Unexpanded nodes get a small "+" badge inviting a click
    nodeElements.filter(d => !d.expanded && !d.loading)
      .append('text')
      .attr('class', 'dg-plus')
      .attr('text-anchor', 'middle')
      .attr('dy', d => d.isRoot ? 8 : 5)
      .attr('fill', d => d.textColor)
      .style('font-size', d => d.isRoot ? '22px' : '14px')
      .style('pointer-events', 'none')
      .text('+');

    nodeElements.append('text')
      .attr('class', 'dg-label')
      .attr('text-anchor', 'middle')
      .attr('dy', d => (d.isRoot ? 26 : 14) + 16)
      .style('pointer-events', 'none')
      .text(d => d.label);

    const simulation = d3.forceSimulation<GNode>(nodes)
      .force('link', d3.forceLink<GNode, any>(links)
        .id((d: any) => d.id)
        .distance(d => 90 + 60 * (1 - Math.min(d.score / maxScore, 1))))
      .force('charge', d3.forceManyBody().strength(-350))
      .force('center', d3.forceCenter(width / 2, height / 2).strength(0.05))
      .force('collide', d3.forceCollide<GNode>().radius(d => (d.isRoot ? 26 : 14) + 26));

    simulation.on('tick', () => {
      linkElements
        .attr('x1', (d: any) => d.source.x)
        .attr('y1', (d: any) => d.source.y)
        .attr('x2', (d: any) => d.target.x)
        .attr('y2', (d: any) => d.target.y);
      nodeElements.attr('transform', d => `translate(${d.x},${d.y})`);
    });

    simulationRef.current = simulation;
    return () => { simulation.stop(); };
  }, [version, expandNode, t]);

  return (
    <div className="discover-graph" ref={containerRef}>
      <svg ref={svgRef} className="discover-graph-svg" />
      {emptyRoot && (
        <div className="discover-graph-empty">
          {t.discover.graphEmpty}
        </div>
      )}
      <div className="discover-graph-hint">
        {t.discover.graphHint}
      </div>
      {tooltip.visible && (
        <div
          className="discover-graph-tooltip"
          style={{ left: tooltip.x, top: tooltip.y }}
          dangerouslySetInnerHTML={{ __html: tooltip.content }}
        />
      )}
    </div>
  );
};

export default DiscoverGraph;
